import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Save, Loader2 } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Profile = () => {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: '', phone: '', address: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setForm({ name: user.name || '', phone: user.phone || '', address: user.address || '' });
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/auth/profile`, form);
      setUser(data);
      toast({ title: 'OK', description: t.dashboard.saved });
    } catch (e) {
      console.error(e);
      toast({ title: 'Hata', description: t.dashboard.saveError });
    } finally {
      setSaving(false);
    }
  };

  const inp = 'bg-white border-zinc-300 text-zinc-900 focus-visible:ring-orange-500';

  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-3xl mx-auto px-5 lg:px-8 py-10">
        <h1 className="text-3xl font-bold text-zinc-900">{t.dashboard.profileTitle}</h1>
        <p className="text-zinc-600 mt-1 mb-6">{t.dashboard.profileSubtitle}</p>

        <Card className="bg-white border-zinc-200 p-6">
          <div className="flex items-center gap-3 mb-5 pb-5 border-b border-zinc-100">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-14 h-14 rounded-full border border-zinc-200" referrerPolicy="no-referrer" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold">
                {user?.name?.[0]?.toUpperCase() || 'R'}
              </div>
            )}
            <div>
              <div className="text-zinc-900 font-semibold">{user?.name}</div>
              <div className="text-zinc-500 text-sm">{user?.email}</div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-700 mb-2 block">{t.dashboard.fullName}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} />
            </div>
            <div>
              <Label className="text-zinc-700 mb-2 block">{t.dashboard.email}</Label>
              <Input value={user?.email || ''} disabled className="bg-zinc-50 border-zinc-200 text-zinc-500" />
            </div>
            <div>
              <Label className="text-zinc-700 mb-2 block">{t.dashboard.phone}</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inp} />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-zinc-700 mb-2 block">{t.dashboard.address}</Label>
              <Textarea rows={3} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inp} />
            </div>
          </div>

          <div className="mt-6">
            <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white border-0">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {t.dashboard.save}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Profile;
